import _ from 'lodash'
import { ANT_UPLOAD_STATUS } from '../../../../constants/globalTypes'
import { filterFileListByError, verifyFileMimeType } from '../../../../lib/uploaderUtil'
import {
  MAIN_AND_REVIEW_IMAGE_COUNT,
  IMAGE_UPLOADER_MAX_FILE,
  IMAGE_UPLOADER_RECOMMENDED_IMAGE_WIDTH_SIZE,
  IMAGE_UPLOADER_MIN_IMAGE_WIDTH_SIZE,
  IMAGE_UPLOADER_MIN_IMAGE_HEIGHT_SIZE,
  IMAGE_UPLOADER_RECOMMENDED_IMAGE_HEIGHT_SIZE
} from '../../Shared'
import { ACCEPT_IMAGE_TYPE_REGEX_WITHOUT_GIF } from '@vzmi/shp_shared_compo/constants/uploader'

export const IMAGE_MIN_DIMENSION = { width: IMAGE_UPLOADER_MIN_IMAGE_WIDTH_SIZE, height: IMAGE_UPLOADER_MIN_IMAGE_HEIGHT_SIZE }
export const IMAGE_RECOMMEND_DIMENSION_TEXT = `${IMAGE_UPLOADER_RECOMMENDED_IMAGE_WIDTH_SIZE}x${IMAGE_UPLOADER_RECOMMENDED_IMAGE_HEIGHT_SIZE} px`
export const MAIN_IMAGE_ERROR_TEXT = `主圖需為${IMAGE_RECOMMEND_DIMENSION_TEXT}`
export const REVIEW_IMAGE_ERROR_TEXT = `送審圖需為${IMAGE_RECOMMEND_DIMENSION_TEXT}`

const UPLOADER_DATA_FIELDS = ['uid', 'status', 'url', 'order']
export const UPLOADER_DUMMY_ERROR_FILES = [{
  uid: 'main-image-error',
  status: ANT_UPLOAD_STATUS.ERROR,
  error: `${MAIN_IMAGE_ERROR_TEXT}，請重新上傳`
},
{
  uid: 'review-image-error',
  status: ANT_UPLOAD_STATUS.ERROR,
  error: `${REVIEW_IMAGE_ERROR_TEXT}，請重新上傳`
}]

const isSquare = (width, height) => {
  const aspectRatio = width / height
  return aspectRatio === 1
}

export const convertProposalImagesToUploaderData = proposalImages => _.map(
  proposalImages,
  image => {
    const dimension = _.pick(image, ['width', 'height']) || {}
    return {
      ...(_.pick(image, UPLOADER_DATA_FIELDS) || {}),
      dimension
    }
  }
)

export const convertUploaderDataToProposalImages = uploaderData => _.map(uploaderData,
  f => {
    return {
      ..._.pick(f, UPLOADER_DATA_FIELDS),
      ...(f.dimension || {})
    }
  })

export const getImgFileVerifierBy = (rule = {}) => file => {
  const { width, height } = _.get(file, 'dimension', { width: 0, height: 0 })

  if (!verifyFileMimeType(file, ACCEPT_IMAGE_TYPE_REGEX_WITHOUT_GIF)) {
    return {
      file,
      error: '圖片格式有誤，僅接受JPG, JPEG, PNG，請確認格式後再重新上傳'
    }
  }

  if (!isSquare(width, height)) {
    return {
      file,
      error: '部分圖片尺寸/格式有誤，僅保留正確的圖片'
    }
  }

  // 上傳圖不可小於 minDimension
  const { minDimension = IMAGE_MIN_DIMENSION.width } = rule
  if (width < minDimension || height < minDimension) {
    return {
      file,
      error: '部分圖片尺寸/格式有誤，僅保留正確的圖片'
    }
  }
  return {
    file
  }
}

export const verifyUploadImage = getImgFileVerifierBy()

export const chunkUploaderDataByDimension = (fileList) => {
  const mainAndReviewImages = getValidMainAndReviewImages(fileList)
  const otherSizeImages = _.differenceBy(fileList, mainAndReviewImages, 'uid')
  return {
    mainAndReviewImages,
    otherSizeImages
  }
}

export const getUploadableSlot = (fileList) => {
  const pureList = filterFileListByError(fileList)
  const {
    mainAndReviewImages,
    otherSizeImages
  } = chunkUploaderDataByDimension(pureList)
  const mainAndReviewImageRemainSlot = MAIN_AND_REVIEW_IMAGE_COUNT - _.size(mainAndReviewImages)
  const otherSizeImageRemainSlot = IMAGE_UPLOADER_MAX_FILE - MAIN_AND_REVIEW_IMAGE_COUNT - _.size(otherSizeImages)
  return [mainAndReviewImageRemainSlot, otherSizeImageRemainSlot]
}

export const getUploadableFileList = (comparableFileList, nextFileList) => {
  const [mainAndReviewImageRemainSlot, otherSizeImageRemainSlot] = getUploadableSlot(comparableFileList)
  let finalList = []
  const verifiedNextFileList = _.filter(nextFileList, f => {
    const r = verifyUploadImage(f)
    return !r.error
  })
  let nextValidLargeSizeImages = []
  if (mainAndReviewImageRemainSlot !== 0) {
    nextValidLargeSizeImages = getValidMainAndReviewImages(verifiedNextFileList)
    nextValidLargeSizeImages = _.take(nextValidLargeSizeImages, mainAndReviewImageRemainSlot)
    finalList = [...finalList, ...nextValidLargeSizeImages]
  }
  if (otherSizeImageRemainSlot !== 0) {
    const nextOtherSizeImages = _.differenceBy(verifiedNextFileList, nextValidLargeSizeImages, 'uid')
    finalList = [...finalList, ..._.take(nextOtherSizeImages, otherSizeImageRemainSlot)]
  }
  return _.compact(finalList)
}

export const getValidMainAndReviewImages = (fileList) => {
  const tempLargeSizeImages = _.filter(fileList, file => {
    const { dimension } = file
    if (_.isEmpty(dimension)) {
      return false
    }
    // 主圖及送審圖必須為正方形且大於1000px * 1000px
    return (
      isSquare(dimension.width, dimension.height) &&
      (dimension.width >= IMAGE_UPLOADER_RECOMMENDED_IMAGE_WIDTH_SIZE && dimension.height >= IMAGE_UPLOADER_RECOMMENDED_IMAGE_HEIGHT_SIZE))
  })
  // 取前兩個已排序過的檔案來重組
  return _.take(tempLargeSizeImages, MAIN_AND_REVIEW_IMAGE_COUNT)
}

export const organizeImageFileList = (fileList) => {
  // once all files are uploaded then start sorting
  const pureList = _.differenceBy(fileList, UPLOADER_DUMMY_ERROR_FILES, 'uid')
  // 取前兩個已排序過的檔案來重組
  let {
    mainAndReviewImages: validLargeSizeImages,
    otherSizeImages
  } = chunkUploaderDataByDimension(pureList)
  otherSizeImages = _.differenceBy(otherSizeImages, UPLOADER_DUMMY_ERROR_FILES, 'uid')
  const validLargeSizeImagesLength = _.size(validLargeSizeImages)

  if (validLargeSizeImagesLength !== MAIN_AND_REVIEW_IMAGE_COUNT) {
    validLargeSizeImages = _.merge([], UPLOADER_DUMMY_ERROR_FILES, validLargeSizeImages)
    validLargeSizeImages = _.reduce(validLargeSizeImages, (acc, lsi, lidx) => {
      if (lidx <= 1) {
        const { error, status } = lsi
        const copiedFile = { ...lsi, error: status === ANT_UPLOAD_STATUS.ERROR ? error : null }
        acc[lidx] = copiedFile
      } else {
        acc[lidx] = lsi
      }
      return acc
    }, [])
  }
  // sort and order
  const finalFileList = [...validLargeSizeImages, ...otherSizeImages]
  return sortImageFileListByOrder(finalFileList)
}

export const sortImageFileListByOrder = (fileList) => {
  return _.reduce(fileList, (acc, f, i) => {
    if (!_.isEmpty(f)) {
      acc.push({
        ...f,
        order: (i + 1)
      })
    }
    return acc
  }, [])
}
